import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { CellContext } from '@tanstack/react-table';

import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { WebEDIRowData } from 'support/types/webediTable';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

import { PRODUCT_KEPT_FIELD, ProductRow, PRODUCTS_DATA_PREFIX } from '../../helpers/productRows';
import { resolveProductLogActor } from './productLogActor';

export const ProductLogCell = <TValue,>({ row }: CellContext<WebEDIRowData, TValue>) => {
  const { t } = useTranslation();
  const log = (row.original as ProductRow).latest_log;
  const isMarkedForDeletion = useWatch({ name: `${PRODUCTS_DATA_PREFIX}.${row.index}.${PRODUCT_KEPT_FIELD}` }) === 0;

  // The audit log makes way for the row's pending fate while it lasts. This is the column that
  // says what has happened to a product; the shared `RowStatus` icon is worded for a document's
  // line items ("Item Confirmed", "Quantity set to 0"), so it can't be reused here.
  if (isMarkedForDeletion) {
    return (
      <div className="flex size-full items-center px-3">
        <Tooltip>
          <TooltipTrigger>
            <NoSymbolIcon className="size-5 text-gray-400" />
          </TooltipTrigger>
          <TooltipContent>{t('settings:productMasterdata.table.markedForDeletion')}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  if (!log) {
    return <div className="flex size-full items-center px-3 text-sm text-gray-900">{EMPTY_FIELD}</div>;
  }

  const actor = resolveProductLogActor(log, t);
  const tooltip = t(
    log.type === 'PRODUCT_CREATED'
      ? 'settings:productMasterdata.table.log.tooltip.created'
      : 'settings:productMasterdata.table.log.tooltip.updated',
    { name: actor, date: formatDayAndTime(log.created_at) },
  );

  return (
    <div className="flex size-full items-center px-3">
      <Tooltip>
        <TooltipTrigger>
          <InformationCircleIcon className="size-4 text-gray-400 hover:text-procuros-green-500" />
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </div>
  );
};

/**
 * i18n keys
 * t('settings:productMasterdata.table.log.tooltip.created', { name, date })
 * t('settings:productMasterdata.table.log.tooltip.updated', { name, date })
 */
